import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import {
    EmptyValue,
    Field,
    FieldGrid,
    MutedValue,
    RecordCard,
    SummaryRow,
    ValueOrEmpty,
} from '@client/components/_ui/display/RecordPrimitives';
import { Callout } from '@client/components/_ui/feedback/Callout';
import { SkeletonBlock } from '@client/components/_ui/feedback/SkeletonBlock';
import { InlineLink } from '@client/components/Common/Link';
import { RepairNetworkOwnerTypeTag } from '@client/components/repair-networks/RepairNetworkOwnerTypeTag';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useHasRouteAccess } from '@client/hooks/navigation/useHasRouteAccess';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { trpc } from '@client/trpc/client';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Building2, ChevronRight, Clock, Map as MapIcon, Network } from 'lucide-react';
import { CLIENT_DETAILS_ROUTE, type RepairNetworkRecord, useNetworkRecord } from './network-record-data';

export const Route = createFileRoute(
    '/console/$orgId/configuration/vendor-management/repair-networks/details/$id/overview',
)({
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <OverviewTab />
        </PageController>
    );
}

function OverviewTab() {
    const { id } = Route.useParams();
    const { data: network } = useNetworkRecord(id);

    if (!network) {
        return <SkeletonBlock variant="card" />;
    }

    return (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="flex min-w-0 flex-col gap-4">
                <DetailsCard network={network} />
                <CoverageCard network={network} />
            </div>
            <div className="flex min-w-0 flex-col gap-4">
                <ClientsCard network={network} />
                <RecordCardDates network={network} />
            </div>
        </div>
    );
}

function DetailsCard({ network }: { network: RepairNetworkRecord }) {
    return (
        <RecordCard icon={Network} title="Network details">
            <FieldGrid columns={3}>
                <Field label="Name">{network.name}</Field>
                <Field label="Owner type">
                    <RepairNetworkOwnerTypeTag value={network.ownerType} />
                </Field>
                <Field label="Owner">
                    <ValueOrEmpty value={network.ownerName} />
                </Field>
                <Field label="Organisation default" span>
                    {network.isDefault
                        ? 'Yes. Clients without their own selection draw their repairers from this network.'
                        : 'No. Only clients that choose this network explicitly draw from it.'}
                </Field>
                <Field label="Description" span>
                    <ValueOrEmpty value={network.description} empty="No description" />
                </Field>
            </FieldGrid>
        </RecordCard>
    );
}

function Stat({ label, value, hint, warn }: { label: string; value: number; hint: string; warn?: boolean }) {
    return (
        <div className="border-separator-secondary px-4 py-3.5 sm:border-r sm:last:border-r-0">
            <div className="font-semibold text-2xs text-subtle-foreground uppercase tracking-wide">{label}</div>
            <div
                className={`mt-0.5 font-semibold text-2xl tabular-nums leading-tight ${warn && value > 0 ? 'text-warning-foreground' : ''}`}
            >
                {value}
            </div>
            <div className="text-subtle-foreground text-xs">{hint}</div>
        </div>
    );
}

function CoverageCard({ network }: { network: RepairNetworkRecord }) {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const notSet = network.coverage.notSet;

    return (
        <RecordCard
            icon={MapIcon}
            title="Coverage"
            subtitle="How members are matched to a vehicle's location"
            padded={false}
        >
            <div className="grid grid-cols-1 sm:grid-cols-3">
                <Stat label="By postcodes" value={network.coverage.postcodes} hint="explicit postcode lists" />
                <Stat label="By radius" value={network.coverage.radius} hint="measured from the repairer's location" />
                <Stat label="Not set" value={notSet} hint="never offered on a claim" warn />
            </div>
            {notSet > 0 ? (
                <div className="border-separator-secondary border-t p-4">
                    <Callout
                        variant="warning"
                        title={`${notSet} ${notSet === 1 ? 'member has' : 'members have'} no coverage`}
                    >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <span>
                                A member without postcodes or a radius is never offered, whatever the distance. Set
                                coverage from the Members tab.
                            </span>
                            <Button
                                type="link"
                                size="small"
                                className="px-0!"
                                onClick={() =>
                                    navigate({
                                        to: '/console/$orgId/configuration/vendor-management/repair-networks/details/$id/members',
                                        params: { orgId, id: network.id },
                                        search: { coverage: 'none' },
                                    })
                                }
                            >
                                Show them
                            </Button>
                        </div>
                    </Callout>
                </div>
            ) : null}
        </RecordCard>
    );
}

function ClientsCard({ network }: { network: RepairNetworkRecord }) {
    const orgId = useOrgId();
    const hasRouteAccess = useHasRouteAccess();
    const canReadClients = hasRouteAccess(
        '/console/$orgId/configuration/vendor-management/repair-networks/details/$id/clients',
    );
    const clientsQuery = trpc.repairNetwork.listRepairNetworkClients.useQuery(
        canReadClients ? { repairNetworkId: network.id, limit: 5 } : skipToken,
    );

    if (!canReadClients) return null;

    return (
        <RecordCard
            icon={Building2}
            title="Clients on this network"
            footer={
                network.clientCount > 5 ? (
                    <InlineLink
                        to="/console/$orgId/configuration/vendor-management/repair-networks/details/$id/clients"
                        params={{ orgId, id: network.id }}
                        className="inline-flex items-center gap-1 text-xs"
                    >
                        View all {network.clientCount} clients
                        <ChevronRight className="size-3" />
                    </InlineLink>
                ) : undefined
            }
        >
            {clientsQuery.isPending ? (
                <SkeletonBlock variant="list" rows={2} />
            ) : clientsQuery.error ? (
                <EmptyValue>Clients could not be loaded.</EmptyValue>
            ) : clientsQuery.data.rows.length === 0 ? (
                <p className="m-0 text-subtle-foreground text-xs">
                    No client draws from this network yet. A client chooses a network from its record; the organisation
                    default serves everyone else.
                </p>
            ) : (
                <div className="flex flex-col">
                    {clientsQuery.data.rows.map((client) => (
                        <SummaryRow
                            key={client.id.toString()}
                            label={
                                <InlineLink to={CLIENT_DETAILS_ROUTE} params={{ orgId, id: client.id }}>
                                    {client.name}
                                </InlineLink>
                            }
                        >
                            <MutedValue>
                                {client.source === 'default' ? 'Organisation default' : 'Own selection'}
                            </MutedValue>
                        </SummaryRow>
                    ))}
                </div>
            )}
        </RecordCard>
    );
}

function RecordCardDates({ network }: { network: RepairNetworkRecord }) {
    const { formatDate, formatDateTime } = useFormatter();
    return (
        <RecordCard icon={Clock} title="Record">
            <div className="flex flex-col">
                <SummaryRow label="Created">{formatDate(network.createdAt)}</SummaryRow>
                <SummaryRow label="Last updated">{formatDateTime(network.updatedAt)}</SummaryRow>
            </div>
        </RecordCard>
    );
}
